import { useRef, useEffect } from "react";

// Horizontal DNA double helix that slowly rotates about its long axis.
// Rendered on a canvas; colour follows the active R/G/B theme accent.
export function DnaHelix({ width = 320, height = 56 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const midY = height / 2;
    const amp = height / 2 - 6;
    const cycles = 2.5;
    const k = (Math.PI * 2 * cycles) / width;

    let phase = 0;
    let raf;

    const accent = () =>
      getComputedStyle(document.documentElement).getPropertyValue("--accent").trim() || "#39FF7A";

    const draw = () => {
      const color = accent();
      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = color;
      ctx.fillStyle = color;

      // rungs (base pairs)
      const step = 14;
      for (let x = step / 2; x <= width; x += step) {
        const y1 = midY + amp * Math.sin(k * x + phase);
        const y2 = midY + amp * Math.sin(k * x + phase + Math.PI);
        const sep = Math.abs(y1 - y2) / (2 * amp); // 0 at crossover, 1 at widest
        ctx.globalAlpha = 0.12 + 0.5 * sep;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, y1);
        ctx.lineTo(x, y2);
        ctx.stroke();
      }

      // two strands, depth-shaded so it reads as a rotating 3D helix
      for (const off of [0, Math.PI]) {
        for (let x = 0; x < width; x += 2) {
          const t1 = k * x + phase + off;
          const y1 = midY + amp * Math.sin(t1);
          const y2 = midY + amp * Math.sin(k * (x + 2) + phase + off);
          const z = (Math.cos(t1) + 1) / 2; // 0 back, 1 front
          ctx.globalAlpha = 0.35 + 0.65 * z;
          ctx.lineWidth = 1 + 1.4 * z;
          ctx.beginPath();
          ctx.moveTo(x, y1);
          ctx.lineTo(x + 2, y2);
          ctx.stroke();
        }
        // node dots at the strand ends of each rung
        for (let x = step / 2; x <= width; x += step) {
          const t = k * x + phase + off;
          const y = midY + amp * Math.sin(t);
          const z = (Math.cos(t) + 1) / 2;
          ctx.globalAlpha = 0.4 + 0.6 * z;
          ctx.beginPath();
          ctx.arc(x, y, 1 + 1.4 * z, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.globalAlpha = 1;
      phase += 0.012; // slow rotation
      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(raf);
  }, [width, height]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="ask-dna-helix"
      style={{ width, height, display: "block" }}
    />
  );
}
